/**
 * "Mis lugares" y el contador para el próximo pase.
 *
 * Aquí se ve la distancia entre poner un pin y publicar (ADR-013). El
 * contador cuenta **recomendaciones con Visit Token**, no pines: si
 * contara pines prometería un pase que el servidor no va a dar, y la
 * mentira se descubriría justo al intentar invitar.
 *
 * Los números vienen del servidor —`published`, `needed`, `invitesLeft`—
 * y no se recalculan aquí: dos cálculos del mismo umbral acaban
 * discrepando.
 */

import { useEffect, useState } from 'react';

import { InstallCard } from '../install/InstallCard.js';
import { debeOfrecer, diasDeUso, type MomentoDeInstalacion } from '../install/install.js';
import { fetchMyPlaces, type MyPlacesResponse } from '../../shared/lib/api.js';
import { Boton, ESPACIO_NAV } from '../../shared/ui/primitives.js';

export interface MyPlacesProps {
  onAddPlace: () => void;
  onOpenPlace?: ((placeId: string) => void) | undefined;
}

export function MyPlaces({ onAddPlace, onOpenPlace }: MyPlacesProps) {
  const [datos, setDatos] = useState<MyPlacesResponse | null>(null);
  const [fallo, setFallo] = useState(false);
  /**
   * El momento de instalación (ADR-015).
   *
   * Se decide AQUÍ y no en el flujo de agregar porque esta es la pantalla
   * de aterrizaje: `docs/03` §6.3 prohíbe pedirlo *durante* el flujo de
   * agregar lugar. Aquí ya terminó, y lo que se ve es el lugar puesto.
   *
   * Los días de uso los cuenta `App` al arrancar; aquí solo se leen.
   */
  const [momento, setMomento] = useState<MomentoDeInstalacion | null>(null);

  useEffect(() => {
    let vivo = true;
    void fetchMyPlaces()
      .then((d) => {
        if (!vivo) return;
        setDatos(d);
        setMomento(
          debeOfrecer({ lugaresPropios: d.places.length, diasDeUso: diasDeUso() }),
        );
      })
      .catch(() => vivo && setFallo(true));
    return () => {
      vivo = false;
    };
  }, []);

  if (fallo) {
    return (
      <Pantalla>
        {/*
         * "No pudimos traerlos" y "no tienes lugares" son estados
         * distintos. Confundirlos hace creer a alguien que perdió lo que
         * había subido.
         */}
        <p role="alert" style={{ margin: 0, color: 'var(--gu-text-primary)' }}>
          No pudimos traer tus lugares. Revisa tu señal e inténtalo otra vez.
        </p>
      </Pantalla>
    );
  }

  if (!datos) {
    return (
      <Pantalla>
        <p style={SECUNDARIO}>Un momento…</p>
      </Pantalla>
    );
  }

  const faltan = Math.max(0, datos.needed - datos.published);

  return (
    <Pantalla>
      <h1 style={TITULO}>Tus lugares</h1>

      {/*
       * El contador. `published` son recomendaciones con token: un pin sin
       * opinión no suma, y decirlo aquí evita la sorpresa al invitar.
       */}
      <section
        style={{
          background: 'var(--gu-surface-raised)',
          borderRadius: 'var(--gu-radius-md)',
          padding: 'var(--gu-space-4)',
        }}
      >
        <strong style={{ fontSize: 'var(--gu-text-h2)' }}>
          {datos.published}/{datos.needed}
        </strong>
        <p style={{ ...SECUNDARIO, marginTop: 'var(--gu-space-1)' }}>
          {datos.invitesLeft > 0 ? (
            <>
              Tienes <strong>{datos.invitesLeft} pases</strong> para traer a
              alguien.
            </>
          ) : faltan === 1 ? (
            'Te falta una opinión para tu próximo pase.'
          ) : (
            `Te faltan ${faltan} opiniones para tu próximo pase.`
          )}
        </p>
      </section>

      {/*
       * El momento de instalación (ADR-015): justo después de agregar el
       * primer lugar, que es donde aterriza el flujo. Va arriba de la
       * lista, no al final: abajo se perdería bajo el pliegue.
       *
       * El nombre del lugar más reciente personaliza el copy, que es lo
       * que hacía bueno el momento original.
       */}
      {momento && (
        <InstallCard
          momento={momento}
          nombreDelLugar={datos.places[0]?.name}
          onCerrar={() => setMomento(null)}
        />
      )}

      {datos.places.length === 0 ? (
        <p style={SECUNDARIO}>
          Todavía no has puesto ningún lugar. El primero que agregues abre el
          mapa para los demás.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--gu-space-2)',
          }}
        >
          {datos.places.map((p) => (
            <li
              key={p.id}
              style={{
                background: 'var(--gu-surface-raised)',
                borderRadius: 'var(--gu-radius-md)',
                padding: 'var(--gu-space-4)',
              }}
            >
              <button
                type="button"
                onClick={() => onOpenPlace?.(p.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: onOpenPlace ? 'pointer' : 'default',
                  width: '100%',
                }}
              >
                <strong>{p.name}</strong>
                {/*
                 * La acción pendiente más valiosa del producto: un lugar
                 * propio sin recomendación es una visita ya hecha que
                 * todavía no cuenta para nada.
                 */}
                {!p.recommendedByMe && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--gu-text-caption)',
                      color: 'var(--gu-text-secondary)',
                    }}
                  >
                    Te falta tu opinión de este lugar
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Boton onClick={onAddPlace}>Agregar un lugar</Boton>
    </Pantalla>
  );
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--gu-surface-app)',
        // El padding inferior reserva el hueco de la barra fija: sin él,
        // "Agregar un lugar" queda debajo y no se puede tocar.
        padding: `var(--gu-space-6) var(--gu-space-5) ${ESPACIO_NAV}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gu-space-4)',
        maxWidth: '32rem',
        marginInline: 'auto',
      }}
    >
      {children}
    </main>
  );
}

const TITULO = {
  fontSize: 'var(--gu-text-h1)',
  lineHeight: 'var(--gu-leading-tight)',
  margin: 0,
} as const;

const SECUNDARIO = { margin: 0, color: 'var(--gu-text-secondary)' } as const;
